import React, { useState } from 'react';
import { Typography, Box, Button, Modal, List, ListItem, ListItemText, Paper, Divider, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { fetchWrapperNoAuth, SERVER } from '../../utils/helpers';
import SelectComponent from '../Inputs/SelectComponent';
import RestoreBatchTutorial from './RestoreBatchTutorial';

const BatchSelectionModal = (props) => {
  const { theme } = useTheme();
  const { user, password } = useAuth();

  const [loadingBatches, setLoadingBatches] = useState(false);
  const [oldBatches, setOldBatches] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [selectedBatchName, setSelectedBatchName] = useState('');

  const styles = {
    modal: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '25vw',
      maxHeight: '70vh',
      bgcolor: theme.background.secondary,
      borderRadius: 2,
      border: `4px solid ${theme.color.primary}`,
      boxShadow: 24,
      p: 4,
      outline: 'none',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      fontFamily: theme.fontFamily,
      textAlign: 'center',
      color: theme.text.primary,
      marginBottom: 2,
    },
    listContainer: {
      maxHeight: '50vh',
      overflow: 'auto',
      marginTop: 2,
      marginBottom: 2,
      borderRadius: 1,
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.color.primary,
        borderRadius: '4px',
      },
    },
    listItem: {
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: `${theme.color.primary}20`,
      },
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    listItemText: {
      cursor: 'pointer',
      flex: 1,
    },
    button: {
      whiteSpace: "nowrap",
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      color: theme.text.negative,
      borderRadius: '20px',
      border: `1px solid ${theme.border.light}`,
      height: "min-content",
      width: "min-content",
      minWidth: "min-content"
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: "center",
      marginTop: 2,
      flexWrap: "wrap",
      gap: 0.5
    },
    noResults: {
      textAlign: 'center',
      padding: 2,
      color: theme.text.secondary,
      fontFamily: theme.fontFamily,
    },
    deleteButton: {
      color: theme.accent.error,
      '&:hover': {
        backgroundColor: `${theme.accent.error}20`,
      },
    }
  };

  const handleSelectOldBatch = async (value) => {
    setSelectedBatchName(value);
    setShowTutorial(true);
  };

  const handleBatchClick = (batch) => {
    props.handleSelectBatch(batch);
    props.setOpen(false);
  };

  const handleRemoveBatch = (event, batch) => {
    event.stopPropagation();
    const confirmed = window.confirm("Are you sure you want to delete this batch?");
    if (confirmed) {
      props.removeBatch(batch);
    }
  };

  const handleRestoreOldBatch = async () => {
    setLoadingBatches(true);
    setShowDropdown(false);
    try {
      const data = await fetchWrapperNoAuth(`${SERVER}/falcon_get_old_batches/${user}`, user, password);
      setOldBatches(data);
      setShowDropdown(true);
    } catch (err) {
      console.error("Error fetching old batches", err);
      setOldBatches([]);
      setShowDropdown(false);
    } finally {
      setLoadingBatches(false);
    }
  };

  const handleCloseTutorial = () => {
    setShowTutorial(false);
    setSelectedBatchName('');
  };

  return <>
    <Modal open={props.open && !showTutorial} onClose={props.cancel}>
      <Paper sx={styles.modal}>
        <Typography variant="h6" component="h2" sx={styles.header}>
          Select Batch in Progress
        </Typography>

        {props.batchesArray.length > 0 ? (
          <Box sx={styles.listContainer}>
            <List>
              {props.batchesArray.map((batch, index) => (
                <React.Fragment key={index}>
                  <ListItem sx={styles.listItem}>
                    <Box
                      sx={styles.listItemText}
                      onClick={() => handleBatchClick(batch)}
                    >
                      <ListItemText
                        primary={batch}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontFamily: theme.fontFamily,
                            color: theme.text.primary
                          }
                        }}
                      />
                    </Box>
                    <Tooltip title={<Typography sx={{ fontFamily: theme.fontFamily, fontSize: '0.7rem' }}>Remove Batch</Typography>}>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        sx={styles.deleteButton}
                        onClick={(e) => handleRemoveBatch(e, batch)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItem>
                  {index < props.batchesArray.length - 1 && <Divider sx={{ bgcolor: theme.accent.disabled }} />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        ) : (
          <Box sx={styles.noResults}>
            <Typography>No batches available</Typography>
          </Box>
        )}

        <Box sx={styles.buttonContainer}>
          {(showDropdown && oldBatches.length) > 0 ? (
            <SelectComponent
              title={"Choose Batch"}
              onChange={handleSelectOldBatch}
              options={oldBatches.map(batch => ({ value: batch, name: batch }))}
              width={"fullWidth"}
            />
          ) : (
            <Button
              variant="contained"
              onClick={handleRestoreOldBatch}
              sx={{ ...styles.button, backgroundColor: theme.accent.success }}
              disabled={loadingBatches || (showDropdown && oldBatches.length < 1)}
            >
              {loadingBatches
                ? "Loading..."
                : (showDropdown && oldBatches.length < 1)
                  ? "No Old Batches"
                  : "Restore Old Batch"}
            </Button>
          )}
          <Button
            variant="contained"
            sx={{ ...styles.button, backgroundColor: theme.accent.success }}
            onClick={props.cancel}
          >
            Start new Batch
          </Button>
        </Box>
      </Paper>
    </Modal>
    <RestoreBatchTutorial
      open={showTutorial}
      onClose={handleCloseTutorial}
      batchName={selectedBatchName}
    />
  </>
};

export default BatchSelectionModal;